import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import NextImage from 'next/image';
import logo from 'shared/assets/logo.svg';
import { BreedAdvisorConversation } from 'shared/components/breed-advisor-conversation';
import { SvgImage } from 'shared/components/svg-image';

export function BreedAdvisor({ children, footer }: { children?: ReactNode; footer?: ReactNode }) {
  const { t } = useTranslation('components');
  return (
    <section
      aria-label={t('breed_advisor.title')}
      className="relative w-full overflow-hidden lg:rounded-lg"
    >
      <picture className="pointer-events-none absolute inset-0 select-none">
        <source
          media="(max-width: 1023px) and (orientation: portrait)"
          type="image/avif"
          srcSet="/images/breed-advisor/mobile-floor-3-418.avif 418w, /images/breed-advisor/mobile-floor-3.avif 836w"
          sizes="100vw"
        />
        <source
          media="(max-width: 1023px) and (orientation: portrait)"
          type="image/webp"
          srcSet="/images/breed-advisor/mobile-floor-3.webp"
        />
        <source
          type="image/avif"
          srcSet="/images/breed-advisor/room-1280.avif 1280w, /images/breed-advisor/room.avif 1672w"
          sizes="(min-width: 1328px) 1280px, calc(100vw - 48px)"
        />
        <NextImage
          src="/images/breed-advisor/room.webp"
          alt=""
          draggable={false}
          fill
          unoptimized
          loading="eager"
          fetchPriority="high"
          className={`select-none object-cover ${footer ? 'object-[85%_top] lg:object-[40%_center]' : 'object-top lg:object-left'}`}
          sizes="(min-width: 1328px) 1280px, (min-width: 1024px) calc(100vw - 48px), 100vw"
        />
      </picture>
      <div className="h-body-without-tabs relative flex min-h-0 flex-col pb-4 lg:block lg:h-auto lg:pb-0 max-lg:landscape:flex-row">
        <div
          aria-hidden="true"
          className="aspect-4/3 max-h-[40%] min-h-0 shrink-0 lg:hidden max-lg:landscape:aspect-auto max-lg:landscape:w-2/5"
        />
        <div className="relative z-10 mx-3 mt-1 flex min-h-0 min-w-0 flex-1 flex-col lg:ml-[52%] lg:mr-0 lg:mt-0 lg:w-[48%] lg:flex-none lg:py-4 lg:pr-4 max-lg:landscape:ml-0 max-lg:landscape:mt-3">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-white lg:h-[42rem] lg:flex-none">
            <div className="min-h-0 flex-1">{children ?? <BreedAdvisorConversation />}</div>
            {footer && <div className="border-brand-brown-dark/10 shrink-0 border-t">{footer}</div>}
          </div>
        </div>
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 hidden w-[52%] select-none justify-center lg:flex">
        <SvgImage
          src={logo}
          alt="Get a Pet"
          containerClassName="aspect-logo w-24"
          imageClassName="select-none object-contain"
        />
      </div>
    </section>
  );
}
